import React, { useState, useEffect, useContext } from 'react';
import ReactDOM from 'react-dom';
import { saveAs } from 'file-saver';
import ExcelJS from 'exceljs';
import CommonDatePicker from '../../../../components/DatePicker/CommonDatePicker';
import { AuthContext } from '../../../../context/AuthContext';
import { getBusinessOrder, saveBusinessOrder, deleteBusinessOrder } from '../../../../api/pmsApi';

// 신규 작성 시 기본 데이터 (기본 항목이 포함된 빈 데이터)
const initialEmptyData = {
  quoteNo: '', invoiceNo: '', invoiceDate: '', salesDate: '', orderType: '', projectName: '', authorName: '',
  sales: [
    { id: 1, isDefault: true, type: 'S/W', amount: 0, invoiceDate: '', depositDate: '', note: '' },
    { id: 2, isDefault: true, type: 'H/W', amount: 0, invoiceDate: '', depositDate: '', note: '' },
    { id: 3, isDefault: true, type: 'Service(내부)', amount: 0, invoiceDate: '', depositDate: '', note: '' },
    { id: 4, isDefault: true, type: 'Service(외주)', amount: 0, invoiceDate: '', depositDate: '', note: '' }
  ],
  purchases: [
    { id: 1, isDefault: true, type: 'S/W', amount: 0, vendor: '', receivedDate: '', paymentDate: '', note: '' },
    { id: 2, isDefault: true, type: 'H/W', amount: 0, vendor: '', receivedDate: '', paymentDate: '', note: '' },
    { id: 3, isDefault: true, type: 'Service(내부)', amount: 0, vendor: '', receivedDate: '', paymentDate: '', note: '' },
    { id: 4, isDefault: true, type: '추가구매(외주)', amount: 0, vendor: '', receivedDate: '', paymentDate: '', note: '' }
  ],
  labor: [
    { id: 1, type: '', assignee: '', mmPlan: 0, period: '', unitPrice: 0, mmActual: 0 },
    { id: 2, type: '', assignee: '', mmPlan: 0, period: '', unitPrice: 0, mmActual: 0 },
    { id: 3, type: '', assignee: '', mmPlan: 0, period: '', unitPrice: 0, mmActual: 0 }
  ],
  actualLabor: [
    { id: 1, role: '', assignee: '', mmActual: 0 },
    { id: 2, role: '', assignee: '', mmActual: 0 },
    { id: 3, role: '', assignee: '', mmActual: 0 },
    { id: 4, role: '', assignee: '', mmActual: 0 }
  ],
  expenses: [
    { id: 1, isDefault: true, type: '숙박료', amount: 0, actual: 0, note: '고정비용' },
    { id: 2, isDefault: true, type: '체류비', amount: 0, actual: 0, note: '체류비(고정비용)' },
    { id: 3, isDefault: true, type: '영업지원비', amount: 0, actual: 0, note: '' },
    { id: 4, isDefault: true, type: '프로젝트휴가', amount: 0, actual: 0, note: '' }
  ],
  description: ''
};

const formatDateToString = (date) => {
  if (!date) return '';
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

export default function BusinessOrderTab({ project, tasks }) {
  const { token, user } = useContext(AuthContext);

  // 권한 판별: PM(작성자/리더) 또는 검토자
  const isPM = React.useMemo(() => {
    if (!user || !project) return false;
    const uId = String(user.id);
    const uLoginId = user.userid ? String(user.userid) : uId;
    return (
      String(project.author_id) === uId || String(project.author_id) === uLoginId ||
      String(project.leader_id) === uId || String(project.leader_id) === uLoginId ||
      String(project.reviewer_id) === uId || String(project.reviewer_id) === uLoginId ||
      user.role === 'admin'
    );
  }, [user, project]);

  // 데이터 상태
  const [data, setData] = useState(initialEmptyData);
  const [originalData, setOriginalData] = useState(null); // 취소 시 롤백용
  const [hasData, setHasData] = useState(false); // DB에 데이터가 존재하는지
  const [mode, setMode] = useState('view');
  const [isCreating, setIsCreating] = useState(false);
  const [loading, setLoading] = useState(true);

  // 프로젝트 ID가 바뀔 때마다 API에서 Business Order 조회
  useEffect(() => {
    const fetchData = async () => {
      if (!project?.id || !token) { setLoading(false); return; }
      try {
        setLoading(true);
        const res = await getBusinessOrder(project.id, token);
        if (res.ok && res.data) {
          const pmName = project.pm_name || project.pm_id || project.leader?.name || project.leader_id || '';
          const allMembersList = [pmName, ...(project.members || []).map(m => m.user?.name || m.user_id)].filter(Boolean);
          
          const filteredActualLabor = (res.data.actualLabor || []).filter(item => {
            if (!item.role && !item.assignee) return true; // Keep empty filler rows
            return allMembersList.includes(item.assignee);
          });

          const fetchedData = {
            ...res.data,
            actualLabor: filteredActualLabor.length > 0 ? filteredActualLabor : initialEmptyData.actualLabor,
            projectName: res.data.projectName || project?.title || '',
            authorName: res.data.authorName || user?.name || ''
          };
          setData(fetchedData);
          setOriginalData(fetchedData);
          setHasData(true);
        } else {
          setData({ ...initialEmptyData, projectName: project?.title || '', authorName: user?.name || '' });
          setOriginalData(null);
          setHasData(false);
        }
      } catch (e) {
        console.error('Business Order 조회 실패:', e);
        setData(initialEmptyData);
        setOriginalData(null);
        setHasData(false);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id, project?.title, token, user?.name]);

  const handleCreateClick = () => {
    setIsCreating(true);
    setMode('edit');
    // 신규 작성 시 기본 빈 데이터로 시작 + 오늘 날짜 설정
    const todayStr = formatDateToString(new Date());
    setData({
      ...initialEmptyData,
      projectName: project?.title || '',
      authorName: user?.name || '',
      salesDate: todayStr
    });
  };

  const handleEditClick = () => setMode('edit');
  const handleCancelClick = () => {
    setMode('view');
    setIsCreating(false);
    // 취소 시 원래 데이터로 롤백
    if (originalData) {
      setData(originalData);
    } else {
      setData(initialEmptyData);
      setHasData(false);
    }
  };
  const handleSaveClick = async () => {
    try {
      const res = await saveBusinessOrder(project.id, data, token);
      if (res.ok && res.data) {
        const savedData = {
          ...res.data,
          projectName: res.data.projectName || project?.title || '',
          authorName: res.data.authorName || user?.name || ''
        };
        setData(savedData);
        setOriginalData(savedData);
        setHasData(true);
        alert('저장되었습니다.');
      } else {
        alert('저장에 실패했습니다.');
      }
    } catch (e) {
      console.error('Business Order 저장 실패:', e);
      alert('저장 중 오류가 발생했습니다.');
    }
    setMode('view');
    setIsCreating(false);
  };

  const handleDeleteClick = async () => {
    if (window.confirm("수주 데이터를 완전히 삭제하시겠습니까? 삭제 시 해당 데이터는 모두 지워지며 초기 화면으로 돌아갑니다.")) {
      try {
        const res = await deleteBusinessOrder(project.id, token);
        if (res.ok) {
          alert('삭제되었습니다.');
          setData(initialEmptyData);
          setOriginalData(null);
          setHasData(false);
          setMode('view');
        } else {
          alert('삭제에 실패했습니다.');
        }
      } catch (e) {
        console.error('Business Order 삭제 실패:', e);
        alert('삭제 중 오류가 발생했습니다.');
      }
    }
  };

  // 아코디언(접기/펼치기) 상태 - 기본적으로 모두 펼쳐짐(false)
  const [collapsed, setCollapsed] = useState({
    info: false,
    sales: false,
    purchases: false,
    labor: false,
    actualLabor: false,
    expenses: false,
    memo: false
  });

  const toggleCollapse = (section) => {
    setCollapsed(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // 실투입 상세내역 모달 관련 State 및 가상데이터
  const [actualLaborModal, setActualLaborModal] = useState({ isOpen: false, userName: '' });
  const [isAllExpanded, setIsAllExpanded] = useState(true);

  const openActualLaborModal = (userName) => {
    if (!userName) return;
    setActualLaborModal({ isOpen: true, userName });
    setIsAllExpanded(true); // 열 때 항상 전체 펼쳐진 상태로 초기화
  };
  const closeActualLaborModal = () => setActualLaborModal({ isOpen: false, userName: '' });

  // 실제 데이터로 변환 (userName 필터링)
  const realUserTasks = (tasks || []).filter(task => {
    const taskMembers = task.members || [];
    return taskMembers.some(m => (m.user?.name || m.user_id) === actualLaborModal.userName);
  }).map(task => {
    const formatStr = (dateStr) => {
      if (!dateStr) return '';
      return dateStr.split('T')[0].replace(/-/g, '.');
    };
    const sDate = formatStr(task.start_date);
    const eDate = formatStr(task.end_date);
    let days = 0;
    if (task.start_date && task.end_date) {
      const diff = Math.abs(new Date(task.end_date) - new Date(task.start_date));
      days = Math.ceil(diff / (1000 * 60 * 60 * 24)) + 1;
    }
    const md = parseFloat(task.man_day) || parseFloat(task.mm) || days || 0;
    
    let prog = task.progress_rate || 0;
    const endDateIso = task.end_date ? task.end_date.split('T')[0] : '';
    const todayIso = new Date().toISOString().split('T')[0];
    
    let statusStr = `시작전(${prog}%)`;
    if (prog === 100) {
      statusStr = `완료(100%)`;
    } else if (endDateIso && endDateIso < todayIso) {
      statusStr = `지연(${prog}%)`;
    } else if (prog > 0) {
      statusStr = `진행중(${prog}%)`;
    }

    // category_name 또는 category 우선, 없으면 일반 업무
    let folder = '지정되지 않은 분류';
    if (task.category_name) folder = task.category_name;
    else if (task.category && task.category.name) folder = task.category.name;
    else if (typeof task.category === 'string') folder = task.category;

    return {
      folder: folder,
      title: task.title || task.name || '제목 없음',
      start: sDate,
      end: eDate,
      status: statusStr,
      md: md
    };
  });

  const groupedRealData = realUserTasks.reduce((acc, curr) => {
    if (!acc[curr.folder]) acc[curr.folder] = [];
    acc[curr.folder].push(curr);
    return acc;
  }, {});

  const totalUserRealMD = realUserTasks.reduce((sum, t) => sum + t.md, 0).toFixed(1);

  // 자동 계산 부분
  const totalLaborAmount = data.labor.reduce((sum, item) => sum + (item.unitPrice * item.mmActual), 0);
  const totalLaborMM = data.labor.reduce((sum, item) => sum + Number(item.mmActual), 0);

  const totalSales = data.sales.reduce((sum, item) => sum + Number(item.amount), 0);

  const purchasesData = data.purchases.map(p => {
    if (p.type === 'Service(내부)') return { ...p, amount: totalLaborAmount };
    return p;
  });
  const totalPurchases = purchasesData.reduce((sum, item) => sum + Number(item.amount), 0);

  const margins = data.sales.map((s, idx) => {
    const pAmount = purchasesData[idx] ? purchasesData[idx].amount : 0;
    return s.amount - pAmount;
  });
  // 각 행별 마진 합계가 아닌, 엑셀 수식과 동일하게 '총매출액 - 총매입액'으로 전체 마진 계산
  const totalMargin = totalSales - totalPurchases;

  const expensesData = data.expenses;
  const totalExpenses = expensesData.reduce((sum, item) => sum + Number(item.amount), 0);
  const totalActualExpenses = expensesData.reduce((sum, item) => sum + Number(item.actual), 0);

  const projectProfit = totalMargin - totalExpenses;
  const projectProfitRate = totalSales > 0 ? ((projectProfit / totalSales) * 100).toFixed(0) : 0;

  const handleAddRow = (section, defaultObj) => {
    setData(prev => ({
      ...prev,
      [section]: [...prev[section], { id: Date.now(), ...defaultObj }]
    }));
  };

  const handleRemoveRow = (section, id) => {
    setData(prev => {
      if (prev[section].length <= 1) {
        alert("최소 1개의 행은 유지되어야 합니다.");
        return prev;
      }
      return {
        ...prev,
        [section]: prev[section].filter(item => item.id !== id)
      };
    });
  };

  const handleLoadActualLabor = (e) => {
    if (e) e.stopPropagation();
    if (!project) return;

    const mmMap = {};
    if (tasks && tasks.length > 0) {
      tasks.forEach(task => {
        let days = 0;
        if (task.start_date && task.end_date) {
          const start = new Date(task.start_date);
          const end = new Date(task.end_date);
          const diffTime = Math.abs(end - start);
          days = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
        }
        const taskMm = parseFloat(task.man_day) || parseFloat(task.mm) || days;

        if (task.members && task.members.length > 0) {
          task.members.forEach(m => {
            if (m.is_manager === true || m.is_manager === 1) {
              const name = m.user?.name || m.user_id || '알수없음';
              mmMap[name] = (mmMap[name] || 0) + taskMm;
            }
          });
        }
      });
    }

    const newActualLabor = [];
    let currentId = Date.now();

    const pmName = project.pm_name || project.pm_id || project.leader?.name || project.leader_id || '';
    if (pmName) {
      newActualLabor.push({ id: currentId++, role: '책임자(PM)', assignee: pmName, mmActual: mmMap[pmName] || 0 });
      delete mmMap[pmName];
    }

    const restMembers = [];
    if (project.members && project.members.length > 0) {
      project.members.forEach(m => {
        const name = m.user?.name || m.user_id;
        if (name && name !== pmName) {
          restMembers.push({ id: currentId++, role: '담당자', assignee: name, mmActual: mmMap[name] || 0 });
          delete mmMap[name];
        }
      });
    }

    restMembers.sort((a, b) => a.assignee.localeCompare(b.assignee));
    newActualLabor.push(...restMembers);

    if (newActualLabor.length === 0) {
      newActualLabor.push({ id: currentId++, role: '', assignee: '', mmActual: 0 });
      newActualLabor.push({ id: currentId++, role: '', assignee: '', mmActual: 0 });
    } else if (newActualLabor.length % 2 !== 0) {
      newActualLabor.push({ id: currentId++, role: '', assignee: '', mmActual: 0 });
    }

    setData(prev => ({ ...prev, actualLabor: newActualLabor }));
  };

  const handleChange = (section, id, field, value) => {
    setData(prev => ({
      ...prev,
      [section]: prev[section].map(item => item.id === id ? { ...item, [field]: value } : item)
    }));
  };

  const handleNumberChange = (section, id, field, rawValue) => {
    const numberValue = Number(rawValue.replace(/[^0-9]/g, ''));
    handleChange(section, id, field, numberValue);
  };

  const formatCurrency = (amount) => amount ? new Intl.NumberFormat('ko-KR').format(amount) : '0';

  // --- 엑셀 다운로드 (수식 및 양식 완벽 적용) ---
  const handleExcelDownload = async () => {
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet('Business Order', { views: [{ showGridLines: false }] });

    // 컬럼 너비 설정 (원본 엑셀과 동일한 비율)
    sheet.columns = [
      { width: 13 }, { width: 13 }, { width: 17 }, { width: 13 },
      { width: 10 }, { width: 12 }, { width: 12 }, { width: 14 }, { width: 14 }
    ];

    const setStyle = (cell, options) => {
      if (options.value !== undefined) cell.value = options.value;
      if (options.font) cell.font = { name: '맑은 고딕', ...options.font };
      if (options.fill) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: options.fill } };
      if (options.align) {
        cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true, ...options.align };
      } else {
        cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
      }
      if (options.border !== false) {
        cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
      }
      if (options.numFmt) cell.numFmt = options.numFmt;
      if (options.formula) cell.value = { formula: options.formula, result: options.result };
    };

    const salesLen = data.sales.length;
    const purchLen = data.purchases.length;
    const laborLen = data.labor.length;
    const expLen = data.expenses.length;

    const rProfit = 10;
    const rSalesTotal = 13;
    const rPurchTotal = rSalesTotal + salesLen + 2;
    const rLaborStart = rPurchTotal + purchLen + 2;
    const rLaborTotal = rLaborStart + laborLen;
    const rExpStart = rLaborTotal + 4;
    const rExpTotal = rExpStart + expLen;

    // 1~2행: 매출품의서 타이틀 영역
    sheet.mergeCells('A1:E1');
    setStyle(sheet.getCell('A1'), { value: '매 출 품 의 서', font: { size: 22, bold: true, underline: true }, border: false });

    // (1차 / 2차 / 3차 / 완료)
    sheet.mergeCells('A2:E2');
    const types = ['1차', '2차', '3차', '완료'];
    const richText = [{ font: { size: 12, bold: true }, text: '( ' }];
    types.forEach((t, i) => {
      const isMatch = (data.orderType || '').includes(t);
      if (isMatch) {
        richText.push({ font: { size: 14, bold: true, color: { argb: 'FFFF0000' } }, text: t });
      } else {
        richText.push({ font: { size: 12, bold: true, color: { argb: 'FF000000' } }, text: t });
      }
      if (i < types.length - 1) richText.push({ font: { size: 12, bold: true }, text: ' / ' });
    });
    richText.push({ font: { size: 12, bold: true }, text: ' )' });
    setStyle(sheet.getCell('A2'), { value: { richText }, border: false });

    // 우측 상단 박스
    sheet.mergeCells('F1:G1'); setStyle(sheet.getCell('F1'), { value: '견적서 NO', fill: 'FFF8CBAD', font: { bold: true } });
    sheet.mergeCells('H1:I1'); setStyle(sheet.getCell('H1'), { value: data.quoteNo || '-' });
    sheet.mergeCells('F2:G2'); setStyle(sheet.getCell('F2'), { value: '계산서 NO / 매출일', fill: 'FFF8CBAD', font: { bold: true } });
    sheet.mergeCells('H2:I2'); setStyle(sheet.getCell('H2'), { value: `${data.invoiceNo || '-'} / ${data.invoiceDate || '-'}` });

    // 결재란
    setStyle(sheet.getCell('G4'), { value: '담당자' });
    setStyle(sheet.getCell('H4'), { value: '확인' });
    setStyle(sheet.getCell('I4'), { value: '대표이사' });
    sheet.mergeCells('G5:G7'); setStyle(sheet.getCell('G5'), { value: '' });
    sheet.mergeCells('H5:H7'); setStyle(sheet.getCell('H5'), { value: '' });
    sheet.mergeCells('I5:I7'); setStyle(sheet.getCell('I5'), { value: '' });

    // 8, 9행: 기본정보
    sheet.mergeCells('A8:B9'); setStyle(sheet.getCell('A8'), { value: '프로젝트 명', fill: 'FFC6E0B4', font: { bold: true } });
    sheet.mergeCells('C8:G9'); setStyle(sheet.getCell('C8'), { value: data.projectName, fill: 'FFC6E0B4', font: { bold: true } });
    setStyle(sheet.getCell('H8'), { value: '작성일 :', fill: 'FFC6E0B4' });
    setStyle(sheet.getCell('I8'), { value: data.salesDate, fill: 'FFC6E0B4' });
    setStyle(sheet.getCell('H9'), { value: '작성자 :', fill: 'FFC6E0B4' });
    setStyle(sheet.getCell('I9'), { value: data.authorName, fill: 'FFC6E0B4' });

    // 10행: 프로젝트 이익
    sheet.mergeCells(`A${rProfit}:B${rProfit}`); setStyle(sheet.getCell(`A${rProfit}`), { value: '프로젝트 이익', fill: 'FFFF99CC', font: { bold: true } });
    sheet.mergeCells(`C${rProfit}:D${rProfit}`); setStyle(sheet.getCell(`C${rProfit}`), { value: '(내부 비용 처리 후)', fill: 'FFFF99CC' });
    sheet.mergeCells(`E${rProfit}:G${rProfit}`);
    setStyle(sheet.getCell(`E${rProfit}`), { formula: `D${rSalesTotal}-D${rExpTotal}`, result: projectProfit, fill: 'FFFF99CC', font: { bold: true }, numFmt: '"₩"#,##0' });
    sheet.mergeCells(`H${rProfit}:I${rProfit}`);
    setStyle(sheet.getCell(`H${rProfit}`), { formula: `IF(C${rSalesTotal}>0, E${rProfit}/C${rSalesTotal}, 0)`, result: (projectProfitRate / 100), fill: 'FFFF99CC', font: { bold: true }, numFmt: '0%' });

    // 11~12행: 매출액 헤더
    sheet.mergeCells('A11:B12'); setStyle(sheet.getCell('A11'), { value: '구 분', fill: 'FFC6E0B4' });
    sheet.mergeCells('C11:C12'); setStyle(sheet.getCell('C11'), { value: '금 액', fill: 'FFC6E0B4' });
    sheet.mergeCells('D11:E11'); setStyle(sheet.getCell('D11'), { value: '마 진', fill: 'FFC6E0B4' });
    setStyle(sheet.getCell('D12'), { value: '금 액', fill: 'FFC6E0B4' });
    setStyle(sheet.getCell('E12'), { value: '비 율', fill: 'FFC6E0B4' });
    sheet.mergeCells('F11:I11'); setStyle(sheet.getCell('F11'), { value: '세금계산서', fill: 'FFC6E0B4' });
    setStyle(sheet.getCell('F12'), { value: '발행일', fill: 'FFC6E0B4' });
    setStyle(sheet.getCell('G12'), { value: '입금일', fill: 'FFC6E0B4' });
    sheet.mergeCells('H12:I12'); setStyle(sheet.getCell('H12'), { value: '내 역', fill: 'FFC6E0B4' });

    // 총매출액
    sheet.mergeCells(`A${rSalesTotal}:A${rSalesTotal + salesLen}`); setStyle(sheet.getCell(`A${rSalesTotal}`), { value: '매출액' });
    setStyle(sheet.getCell(`B${rSalesTotal}`), { value: '총매출액', fill: 'FFFFFF00', font: { bold: true } });
    setStyle(sheet.getCell(`C${rSalesTotal}`), { formula: `SUM(C${rSalesTotal + 1}:C${rSalesTotal + salesLen})`, result: totalSales, fill: 'FFFFFF00', font: { bold: true, color: { argb: 'FF0000FF' } }, numFmt: '"₩"#,##0' });
    setStyle(sheet.getCell(`D${rSalesTotal}`), { formula: `C${rSalesTotal}-C${rPurchTotal}`, result: totalMargin, fill: 'FFFFFF00', font: { bold: true, color: { argb: 'FF0000FF' } }, numFmt: '"₩"#,##0' });
    setStyle(sheet.getCell(`E${rSalesTotal}`), { formula: `IF(C${rSalesTotal}>0, D${rSalesTotal}/C${rSalesTotal}, 0)`, result: totalSales > 0 ? (totalMargin / totalSales) : 0, fill: 'FFFFFF00', font: { bold: true }, numFmt: '0%' });
    setStyle(sheet.getCell(`F${rSalesTotal}`), { value: '', fill: 'FFFFFF00' });
    setStyle(sheet.getCell(`G${rSalesTotal}`), { value: '', fill: 'FFFFFF00' });
    sheet.mergeCells(`H${rSalesTotal}:I${rSalesTotal}`); setStyle(sheet.getCell(`H${rSalesTotal}`), { value: '', fill: 'FFFFFF00' });

    let r = rSalesTotal + 1;
    data.sales.forEach((s, idx) => {
      const pRow = rPurchTotal + 1 + idx; // 매칭되는 매입액 행
      setStyle(sheet.getCell(`B${r}`), { value: s.type });
      setStyle(sheet.getCell(`C${r}`), { value: Number(s.amount), numFmt: '"₩"#,##0' });
      setStyle(sheet.getCell(`D${r}`), { formula: `C${r}-C${pRow}`, result: margins[idx], numFmt: '"₩"#,##0' });
      setStyle(sheet.getCell(`E${r}`), { formula: `IF(C${r}>0, D${r}/C${r}, "")`, result: Number(s.amount) > 0 ? (margins[idx] / Number(s.amount)) : '', numFmt: '0%' });
      setStyle(sheet.getCell(`F${r}`), { value: s.invoiceDate });
      setStyle(sheet.getCell(`G${r}`), { value: s.depositDate });
      sheet.mergeCells(`H${r}:I${r}`); setStyle(sheet.getCell(`H${r}`), { value: s.note, align: { horizontal: 'left' } });
      r++;
    });

    // 매입액 헤더
    r = rPurchTotal - 1;
    sheet.mergeCells(`A${r}:B${r}`); setStyle(sheet.getCell(`A${r}`), { value: '구 분', fill: 'FFC6E0B4' });
    setStyle(sheet.getCell(`C${r}`), { value: '금 액', fill: 'FFC6E0B4' });
    sheet.mergeCells(`D${r}:E${r}`); setStyle(sheet.getCell(`D${r}`), { value: '발주처', fill: 'FFC6E0B4' });
    sheet.mergeCells(`F${r}:I${r}`); setStyle(sheet.getCell(`F${r}`), { value: '세금계산서', fill: 'FFC6E0B4' });

    // 총매입액
    r = rPurchTotal;
    sheet.mergeCells(`A${r}:A${r + purchLen}`); setStyle(sheet.getCell(`A${r}`), { value: '매입액' });
    setStyle(sheet.getCell(`B${r}`), { value: '총매입액', fill: 'FFFFFF00', font: { bold: true } });
    setStyle(sheet.getCell(`C${r}`), { formula: `SUM(C${r + 1}:C${r + purchLen})`, result: totalPurchases, fill: 'FFFFFF00', font: { bold: true, color: { argb: 'FFFF0000' } }, numFmt: '"₩"#,##0' });
    sheet.mergeCells(`D${r}:E${r}`); setStyle(sheet.getCell(`D${r}`), { value: '', fill: 'FFFFFF00' });
    setStyle(sheet.getCell(`F${r}`), { value: '접수일', fill: 'FFFFFF00' });
    setStyle(sheet.getCell(`G${r}`), { value: '대금결제일', fill: 'FFFFFF00' });
    sheet.mergeCells(`H${r}:I${r}`); setStyle(sheet.getCell(`H${r}`), { value: '내 역', fill: 'FFFFFF00' });
    r++;

    purchasesData.forEach((p, idx) => {
      setStyle(sheet.getCell(`B${r}`), { value: p.type });
      if (p.type === 'Service(내부)') {
        setStyle(sheet.getCell(`C${r}`), { formula: `I${rLaborTotal}`, result: Number(p.amount), numFmt: '"₩"#,##0' });
      } else {
        setStyle(sheet.getCell(`C${r}`), { value: Number(p.amount), numFmt: '"₩"#,##0' });
      }
      sheet.mergeCells(`D${r}:E${r}`); setStyle(sheet.getCell(`D${r}`), { value: p.vendor });
      setStyle(sheet.getCell(`F${r}`), { value: p.receivedDate });
      setStyle(sheet.getCell(`G${r}`), { value: p.paymentDate });
      sheet.mergeCells(`H${r}:I${r}`); setStyle(sheet.getCell(`H${r}`), { value: p.note, align: { horizontal: 'left' } });
      r++;
    });

    // 서비스 매출
    r = rLaborStart - 1;
    sheet.mergeCells(`A${r}:A${r + laborLen + 1}`); setStyle(sheet.getCell(`A${r}`), { value: '서비스\n매출' });
    sheet.mergeCells(`B${r}:C${r}`); setStyle(sheet.getCell(`B${r}`), { value: '구 분', fill: 'FFC6E0B4' });
    setStyle(sheet.getCell(`D${r}`), { value: '담 당', fill: 'FFC6E0B4' });
    setStyle(sheet.getCell(`E${r}`), { value: 'M/M', fill: 'FFC6E0B4' });
    setStyle(sheet.getCell(`F${r}`), { value: '투입 기간', fill: 'FFC6E0B4' });
    setStyle(sheet.getCell(`G${r}`), { value: '단 가', fill: 'FFC6E0B4' });
    setStyle(sheet.getCell(`H${r}`), { value: '실 투입MM', fill: 'FFC6E0B4' });
    setStyle(sheet.getCell(`I${r}`), { value: '금 액', fill: 'FFC6E0B4' });

    r = rLaborStart;
    data.labor.forEach(l => {
      sheet.mergeCells(`B${r}:C${r}`); setStyle(sheet.getCell(`B${r}`), { value: l.type });
      setStyle(sheet.getCell(`D${r}`), { value: l.assignee });
      setStyle(sheet.getCell(`E${r}`), { value: Number(l.mmPlan), numFmt: '#,##0.00' });
      setStyle(sheet.getCell(`F${r}`), { value: l.period });
      setStyle(sheet.getCell(`G${r}`), { value: Number(l.unitPrice), numFmt: '#,##0' });
      setStyle(sheet.getCell(`H${r}`), { value: Number(l.mmActual), numFmt: '#,##0.00' });
      setStyle(sheet.getCell(`I${r}`), { formula: `G${r}*H${r}`, result: Number(l.unitPrice) * Number(l.mmActual), numFmt: '#,##0' });
      r++;
    });

    // 총투입공수
    r = rLaborTotal;
    sheet.mergeCells(`B${r}:D${r}`); setStyle(sheet.getCell(`B${r}`), { value: '총투입공수' });
    setStyle(sheet.getCell(`E${r}`), { formula: `SUM(E${rLaborStart}:E${rLaborTotal - 1})`, result: totalLaborMM, numFmt: '#,##0.00' });
    setStyle(sheet.getCell(`F${r}`), { value: '' });
    setStyle(sheet.getCell(`G${r}`), { value: 1, numFmt: '0%' }); // 100%
    setStyle(sheet.getCell(`H${r}`), { formula: `SUM(H${rLaborStart}:H${rLaborTotal - 1})`, result: totalLaborMM, numFmt: '#,##0.00' });
    setStyle(sheet.getCell(`I${r}`), { formula: `SUM(I${rLaborStart}:I${rLaborTotal - 1})`, result: totalLaborAmount, numFmt: '#,##0' });

    // 기타비용
    r = rExpStart - 2;
    sheet.mergeCells(`A${r}:A${r + expLen + 2}`); setStyle(sheet.getCell(`A${r}`), { value: '기타비용' });
    sheet.mergeCells(`B${r}:C${r + 1}`); setStyle(sheet.getCell(`B${r}`), { value: '구 분', fill: 'FFC6E0B4' });
    sheet.mergeCells(`D${r}:D${r + 1}`); setStyle(sheet.getCell(`D${r}`), { value: '금 액', fill: 'FFC6E0B4' });
    sheet.mergeCells(`E${r}:I${r}`); setStyle(sheet.getCell(`E${r}`), { value: '내 역', fill: 'FFC6E0B4' });
    setStyle(sheet.getCell(`E${r + 1}`), { value: '실사용금액', fill: 'FFC6E0B4' });
    setStyle(sheet.getCell(`F${r + 1}`), { value: '차액', fill: 'FFC6E0B4' });
    setStyle(sheet.getCell(`G${r + 1}`), { value: '잔액', fill: 'FFC6E0B4' });
    sheet.mergeCells(`H${r + 1}:I${r + 1}`); setStyle(sheet.getCell(`H${r + 1}`), { value: '비 고', fill: 'FFC6E0B4' });

    r = rExpStart;
    data.expenses.forEach(e => {
      sheet.mergeCells(`B${r}:C${r}`); setStyle(sheet.getCell(`B${r}`), { value: e.type });
      setStyle(sheet.getCell(`D${r}`), { value: Number(e.amount), numFmt: '"₩"#,##0' });
      setStyle(sheet.getCell(`E${r}`), { value: Number(e.actual), numFmt: '"₩"#,##0' });
      setStyle(sheet.getCell(`F${r}`), { formula: `D${r}-E${r}`, result: Number(e.amount) - Number(e.actual), numFmt: '"₩"#,##0' });
      setStyle(sheet.getCell(`G${r}`), { formula: `D${r}-E${r}`, result: Number(e.amount) - Number(e.actual), numFmt: '"₩"#,##0' });
      sheet.mergeCells(`H${r}:I${r}`); setStyle(sheet.getCell(`H${r}`), { value: e.note, align: { horizontal: 'left' } });
      r++;
    });

    // 합계
    r = rExpTotal;
    sheet.mergeCells(`B${r}:C${r}`); setStyle(sheet.getCell(`B${r}`), { value: '합계' });
    setStyle(sheet.getCell(`D${r}`), { formula: `SUM(D${rExpStart}:D${rExpTotal - 1})`, result: totalExpenses, numFmt: '"₩"#,##0' });
    setStyle(sheet.getCell(`E${r}`), { formula: `SUM(E${rExpStart}:E${rExpTotal - 1})`, result: totalActualExpenses, numFmt: '"₩"#,##0' });
    setStyle(sheet.getCell(`F${r}`), { formula: `SUM(F${rExpStart}:F${rExpTotal - 1})`, result: totalExpenses - totalActualExpenses, numFmt: '"₩"#,##0' });
    setStyle(sheet.getCell(`G${r}`), { formula: `SUM(G${rExpStart}:G${rExpTotal - 1})`, result: totalExpenses - totalActualExpenses, numFmt: '"₩"#,##0' });
    sheet.mergeCells(`H${r}:I${r}`); setStyle(sheet.getCell(`H${r}`), { value: '' });

    // 특이사항 및 메모 (description)
    r = rExpTotal + 2;
    sheet.mergeCells(`A${r}:I${r + 5}`);
    setStyle(sheet.getCell(`A${r}`), {
      value: data.description || '',
      align: { horizontal: 'left', vertical: 'top', wrapText: true },
      font: { size: 10 },
      border: false
    });

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer], { type: 'application/octet-stream' }), `매출품의서_${data.projectName}.xlsx`);
  };

  const inputStyle = {
    width: '100%',
    height: '100%',
    backgroundColor: 'transparent',
    transition: 'all 0.2s ease',
    outline: 'none',
    boxShadow: 'none'
  };

  return (
    <div className="d-flex flex-column h-100 p-4 bg-light rounded-3 shadow-sm border border-secondary border-opacity-10 animation-fade-in" style={{ overflowY: 'auto' }}>

      {/* 커스텀 스타일 (고급 그리드 & CommonDatePicker 연동 & 아코디언) */}
      <style>{`
        /* 숫자 입력칸 스피너(위아래 화살표) 숨김 처리 (중앙정렬용) */
        input[type="number"]::-webkit-outer-spin-button,
        input[type="number"]::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        input[type="number"] {
          -moz-appearance: textfield;
        }
        .seamless-input::placeholder,
        .form-control::placeholder {
          color: #adb5bd !important;
          opacity: 0.3 !important;
          font-weight: 400 !important;
        }
        .seamless-input:focus {
          background-color: #f8f9fa !important;
          outline: 1px solid #667eea !important;
          z-index: 10;
        }
        .table-custom-border {
          border-color: #dee2e6 !important;
        }
        .table-custom-border td, .table-custom-border th {
          border-color: #dee2e6 !important;
        }
        .grid-datepicker .react-datepicker-wrapper {
          width: 100%;
          height: 100%;
        }
        .grid-datepicker .form-control {
          border: none !important;
          border-radius: 0 !important;
          text-align: center;
          width: 100%;
          height: 100%;
          background-color: transparent !important;
          padding: 0.5rem 0.25rem !important;
          color: #6c757d;
          font-weight: 500;
          font-size: 0.85rem;
        }
        .grid-datepicker .form-control:focus {
          background-color: #f8f9fa !important;
          outline: 1px solid #667eea !important;
          box-shadow: none !important;
          z-index: 10;
          position: relative;
        }
        .accordion-icon {
          transition: transform 0.3s ease;
        }
        .accordion-icon.collapsed {
          transform: rotate(-90deg);
        }
        .modern-date-wrapper .react-datepicker-wrapper,
        .pms-datepicker-container .react-datepicker-wrapper {
          width: 100%;
        }
        
        /* 💡 수정(Edit) 모드일 때 금액 입력 칸에만 연한 배경색 처리 */
        :not(.mode-view) .table input.amount-input:not([readonly]) {
          background-color: #f4f6f8 !important;
          border: 1px dashed #cbd5e1 !important;
          border-radius: 6px !important;
          transition: all 0.2s ease;
        }
        
        /* 입력 불가능한 칸(readonly)은 투명하게 유지 */
        :not(.mode-view) .table input.amount-input[readonly] {
          background-color: transparent !important;
          border: none !important;
          color: #475569 !important;
        }

        .hover-delete-btn { opacity: 0; transition: opacity 0.2s; position: absolute; right: 6px; top: 50%; transform: translateY(-50%); z-index: 10; background: rgba(255,255,255,0.95); border-radius: 4px; border: none; padding: 4px 8px; box-shadow: 0 1px 4px rgba(0,0,0,0.15); }
        tr:hover .hover-delete-btn { opacity: 1; }
        .actual-labor-table tr:hover .hover-delete-btn { opacity: 0; }
        .actual-labor-table td.position-relative:hover .hover-delete-btn { opacity: 1; }
        
        /* 조회 모드 전용 스타일: 안정감을 주기 위해 비활성화(Disabled) 느낌으로 통일 */
        .mode-view input, .mode-view select, .mode-view .grid-datepicker .form-control, .mode-view .modern-date-wrapper input {
          pointer-events: none !important;
          background-color: #f8f9fa !important; /* 연한 회색 배경 */
          border: 1px solid #e9ecef !important; /* 연한 테두리 */
          box-shadow: none !important;
          color: #6c757d !important; /* 텍스트도 살짝 어둡게 */
          font-weight: 600 !important;
        }

        /* 표(그리드) 내부는 셀 자체가 구조를 잡아주므로 박스 느낌을 빼고 투명하게 스며들도록 오버라이드 */
        .mode-view .table input, .mode-view .table select, .mode-view .table .grid-datepicker .form-control {
          background-color: transparent !important;
          border: none !important;
        }
        
        .mode-view select {
          -webkit-appearance: none;
          -moz-appearance: none;
          appearance: none;
          background-image: none !important;
        }
        
        /* 조회모드에서 달력 아이콘 숨김 */
        .mode-view .fa-calendar-alt {
          display: none !important;
        }

        .mode-view input::placeholder {
          color: transparent !important;
        }
        
        /* 조회모드일 때 추가 행/삭제 버튼 숨기기 */
        .mode-view .hover-delete-btn, .mode-view .btn-sm {
          display: none !important;
        }
      `}</style>

      {/* 1. 상단 타이틀 영역 */}
      <div className="d-flex justify-content-between align-items-center mb-4 pb-3 border-bottom border-secondary border-opacity-10">
        <div>
          <h4 className="fw-bolder text-dark mb-1"><i className="fas fa-file-invoice-dollar text-primary me-2"></i>Business Order</h4>
          <span className="text-secondary fs-7">프로젝트의 매출/매입 및 비용을 관리합니다.</span>
        </div>
        <div className="d-flex gap-2">
          {mode === 'view' ? (
            <>
              {(!isPM || hasData) && hasData && (
                <button className="btn btn-success fw-bold px-4 py-2 rounded-3 shadow-sm" onClick={handleExcelDownload}>
                  <i className="fas fa-file-excel me-2"></i>Excel 다운로드
                </button>
              )}
              {isPM && hasData && (
                <button className="btn btn-danger fw-bold px-4 py-2 rounded-3 shadow-sm" onClick={handleDeleteClick}>
                  <i className="fas fa-trash-alt me-2"></i>삭제
                </button>
              )}
              {isPM && (
                <button className="btn btn-primary fw-bold px-4 py-2 rounded-3 shadow-sm" onClick={hasData ? handleEditClick : handleCreateClick}>
                  <i className={hasData ? "fas fa-edit me-2" : "fas fa-pencil-alt me-2"}></i>{hasData ? '수정' : '신규 작성'}
                </button>
              )}
            </>
          ) : (
            <>
              <button className="btn btn-secondary fw-bold px-4 py-2 rounded-3 shadow-sm border-0 text-white" onClick={handleCancelClick}>
                취소
              </button>
              <button className="btn btn-primary fw-bold px-4 py-2 rounded-3 shadow-sm" onClick={handleSaveClick}>
                <i className="fas fa-save me-2"></i>저장
              </button>
            </>
          )}
        </div>
      </div>

      {/* 로딩 중, 데이터 없음, 데이터 있음 상태에 따른 분기 */}
      {loading ? (
        <div className="text-center mt-2 d-flex flex-column align-items-center justify-content-center py-5" style={{ borderRadius: '1rem', backgroundColor: '#f8fafc', minHeight: '280px' }}>
          <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem' }} role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <h5 className="fw-bold text-secondary">데이터를 불러오는 중입니다...</h5>
        </div>
      ) : (!hasData && !isCreating) ? (
        <div className="text-center mt-2 d-flex flex-column align-items-center justify-content-center py-5" style={{ border: '2px dashed #e2e8f0', borderRadius: '1rem', backgroundColor: '#f8fafc', minHeight: '280px' }}>
          <div className="mb-3 text-secondary opacity-50">
            <i className="fas fa-file-invoice-dollar" style={{ fontSize: '2.5rem' }}></i>
          </div>
          <h5 className="fw-bold text-secondary mb-2">등록된 Business Order가 없습니다.</h5>
          <p className="text-muted fs-7 mb-4" style={{ maxWidth: '500px' }}>
            {isPM ? "프로젝트의 매출, 매입 및 기타 비용을 통합적으로 관리할 수 있습니다. 아래 버튼이나 우측 상단 버튼을 통해 신규 작성을 시작해 보세요." : "담당자는 열람만 가능하며, 새로운 작성은 책임자(PM) 또는 검토자 권한이 필요합니다."}
          </p>
          {isPM && (
            <button className="btn btn-outline-primary fw-bold px-4 py-2 rounded-3" onClick={handleCreateClick}>
              <i className="fas fa-plus me-2"></i>신규 작성
            </button>
          )}
        </div>
      ) : (
        <>
          {/* 2. 상단 핵심 요약 지표 */}
          <div className="row g-3 mb-4">
            <div className="col-md-3">
              <div className="card border-0 shadow-sm rounded-4 h-100 p-4 text-white" style={{ background: 'linear-gradient(135deg, #00c6ff 0%, #0072ff 100%)' }}>
                <span className="text-white-50 fw-bold mb-2 fs-7">총 매출액</span>
                <h3 className="fw-bolder text-white mb-0 tracking-tight">₩{formatCurrency(totalSales)}</h3>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card border-0 shadow-sm rounded-4 h-100 p-4 text-white" style={{ background: 'linear-gradient(135deg, #ff758c 0%, #ff7eb3 100%)' }}>
                <span className="text-white-50 fw-bold mb-2 fs-7">총 매입액</span>
                <h3 className="fw-bolder text-white mb-0 tracking-tight">₩{formatCurrency(totalPurchases)}</h3>
              </div>
            </div>
            <div className="col-md-6">
              <div className="card border-0 shadow-sm rounded-4 h-100 p-4 text-white" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                <div className="d-flex justify-content-between align-items-center h-100">
                  <div>
                    <span className="text-white-50 fw-bold mb-2 fs-7">프로젝트 이익 (내부 비용 처리 후)</span>
                    <h2 className="fw-bolder text-white mb-0 tracking-tight">₩{formatCurrency(projectProfit)}</h2>
                  </div>
                  <div className="text-end border-start border-light border-opacity-25 ps-4">
                    <span className="text-white-50 fw-bold mb-2 fs-7">마진율</span>
                    <h2 className="fw-bolder text-white mb-0">{projectProfitRate}%</h2>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className={mode === 'view' ? "row g-2 mode-view" : "row g-2"}>

            {/* 3. 기본 정보 */}
            <div className="col-12">
              <div className="card border-0 shadow-sm rounded-4 bg-white overflow-hidden">
                <div
                  className={`card-header bg-white rounded-top-4 ${collapsed.info ? 'border-bottom-0' : 'border-bottom'} py-3 px-4 d-flex align-items-center`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => toggleCollapse('info')}
                >
                  <i className={`fas fa-chevron-down text-secondary me-2 accordion-icon ${collapsed.info ? 'collapsed' : ''}`}></i>
                  <h5 className="fw-bolder text-dark m-0"><i className="fas fa-info-circle text-primary me-2"></i>기본 정보</h5>
                </div>
                {!collapsed.info && (
                  <div className="card-body px-4 pb-4 slide-down-fade">
                    <div className="bg-light bg-opacity-50 p-4 rounded-4 border border-secondary border-opacity-10">
                      {/* 첫 번째 줄 */}
                      <div className="row g-3 mb-3">
                        <div className="col-md-6">
                          <label className="form-label fs-9 fw-bold text-muted mb-1 text-uppercase tracking-wide"><i className="fas fa-folder-open me-1 opacity-75 text-primary"></i>프로젝트 명</label>
                          <input type="text" className="form-control form-control-sm border-secondary border-opacity-25 px-3 py-2 fw-bolder text-primary rounded-3 bg-white shadow-sm" value={data.projectName} onChange={e => setData({ ...data, projectName: e.target.value })} />
                        </div>
                        <div className="col-md-3">
                          <label className="form-label fs-9 fw-bold text-muted mb-1 text-uppercase tracking-wide"><i className="fas fa-user-edit me-1 opacity-75 text-success"></i>작성자</label>
                          <input type="text" className="form-control form-control-sm border-secondary border-opacity-25 px-3 py-2 text-dark fw-bold rounded-3 bg-white shadow-sm" value={data.authorName} onChange={e => setData({ ...data, authorName: e.target.value })} />
                        </div>
                        <div className="col-md-3 modern-date-wrapper">
                          <label className="form-label fs-9 fw-bold text-muted mb-1 text-uppercase tracking-wide"><i className="fas fa-clock me-1 opacity-75 text-info"></i>작성일</label>
                          <CommonDatePicker
                            value={data.salesDate ? new Date(data.salesDate) : null}
                            onChange={(d) => setData({ ...data, salesDate: formatDateToString(d) })}
                            className="form-control-sm border border-secondary border-opacity-25 px-3 py-2 text-dark fw-bold rounded-3 bg-white shadow-sm w-100"
                            placeholder="작성일"
                          />
                        </div>
                      </div>
                      {/* 두 번째 줄 */}
                      <div className="row g-3">
                        <div className="col-md-3">
                          <label className="form-label fs-9 fw-bold text-muted mb-1 text-uppercase tracking-wide"><i className="fas fa-file-alt me-1 opacity-75 text-secondary"></i>견적서 NO</label>
                          <input type="text" className="form-control form-control-sm border-secondary border-opacity-25 px-3 py-2 text-dark fw-bold rounded-3 bg-white shadow-sm" value={data.quoteNo} onChange={e => setData({ ...data, quoteNo: e.target.value })} />
                        </div>
                        <div className="col-md-3">
                          <label className="form-label fs-9 fw-bold text-muted mb-1 text-uppercase tracking-wide"><i className="fas fa-receipt me-1 opacity-75 text-secondary"></i>계산서 NO</label>
                          <input type="text" className="form-control form-control-sm border-secondary border-opacity-25 px-3 py-2 text-dark fw-bold rounded-3 bg-white shadow-sm" value={data.invoiceNo} onChange={e => setData({ ...data, invoiceNo: e.target.value })} />
                        </div>
                        <div className="col-md-3 modern-date-wrapper">
                          <label className="form-label fs-9 fw-bold text-muted mb-1 text-uppercase tracking-wide"><i className="fas fa-calendar-check me-1 opacity-75 text-warning"></i>매출일</label>
                          <CommonDatePicker
                            value={data.invoiceDate ? new Date(data.invoiceDate) : null}
                            onChange={(d) => setData({ ...data, invoiceDate: formatDateToString(d) })}
                            className="form-control-sm border border-secondary border-opacity-25 px-3 py-2 text-dark fw-bold rounded-3 bg-white shadow-sm w-100"
                            placeholder="매출일"
                          />
                        </div>
                        <div className="col-md-3">
                          <label className="form-label fs-9 fw-bold text-muted mb-1 text-uppercase tracking-wide"><i className="fas fa-layer-group me-1 opacity-75 text-secondary"></i>진행 차수</label>
                          <select className="form-select form-select-sm border-secondary border-opacity-25 px-3 py-2 text-dark fw-bold rounded-3 bg-white shadow-sm" value={data.orderType} onChange={e => setData({ ...data, orderType: e.target.value })}>
                            <option value="1차">1차</option>
                            <option value="2차">2차</option>
                            <option value="3차">3차</option>
                            <option value="완료">완료</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* 4. 매출액 상세 */}
            <div className="col-12">
              <div className="card border-0 shadow-sm rounded-4 bg-white overflow-hidden">
                <div
                  className={`card-header bg-white rounded-top-4 ${collapsed.sales ? 'border-bottom-0' : 'border-bottom'} py-3 px-4 d-flex justify-content-between align-items-center`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => toggleCollapse('sales')}
                >
                  <div className="d-flex align-items-center">
                    <i className={`fas fa-chevron-down text-secondary me-2 accordion-icon ${collapsed.sales ? 'collapsed' : ''}`}></i>
                    <h5 className="fw-bolder m-0" style={{ color: '#0072ff' }}><i className="fas fa-arrow-circle-up me-2"></i>매출액 상세</h5>
                  </div>
                  {mode === 'edit' && (
                    <button className="btn btn-sm fw-bold rounded-pill px-3 shadow-sm" style={{ backgroundColor: '#e6f0ff', color: '#0072ff' }} onClick={(e) => { e.stopPropagation(); handleAddRow('sales', { type: '', amount: 0, invoiceDate: '', depositDate: '', note: '' }); }}><i className="fas fa-plus me-1"></i>행 추가</button>
                  )}
                </div>
                {!collapsed.sales && (
                  <div className="card-body p-0 slide-down-fade">
                    <div className="table-responsive">
                      <table className="table table-bordered table-custom-border text-center align-middle mb-0" style={{ borderCollapse: 'collapse' }}>
                        <thead className="bg-light">
                          <tr>
                            <th rowSpan="2" className="py-2 px-2 fw-bolder fs-8 text-secondary align-middle bg-light" style={{ width: '15%' }}>구분</th>
                            <th rowSpan="2" className="py-2 px-2 fw-bolder fs-8 text-secondary align-middle bg-light" style={{ width: '15%' }}>금액</th>
                            <th colSpan="2" className="py-1 px-2 fw-bolder fs-8 text-secondary bg-light border-bottom-0">마진</th>
                            <th colSpan="3" className="py-1 px-2 fw-bolder fs-8 text-secondary bg-light border-bottom-0">세금계산서</th>
                          </tr>
                          <tr>
                            <th className="py-1 px-2 fw-bold fs-9 text-muted bg-light border-top-0" style={{ width: '13%' }}>금액</th>
                            <th className="py-1 px-2 fw-bold fs-9 text-muted bg-light border-top-0" style={{ width: '7%' }}>비율(%)</th>
                            <th className="py-1 px-2 fw-bold fs-9 text-muted bg-light border-top-0" style={{ width: '12%' }}>발행일</th>
                            <th className="py-1 px-2 fw-bold fs-9 text-muted bg-light border-top-0" style={{ width: '12%' }}>입금일</th>
                            <th className="py-1 px-2 fw-bold fs-9 text-muted bg-light border-top-0">내역</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="border-bottom border-secondary border-opacity-10 bg-white">
                            <td className="p-2 border-0">
                              <div className="bg-secondary bg-opacity-10 rounded-3 py-2 px-2 w-100 fw-bold text-secondary fs-8 d-flex align-items-center justify-content-center">
                                총매출액
                              </div>
                            </td>
                            <td className="p-2 border-0">
                              <div className="rounded-3 py-2 px-3 w-100 fw-bolder fs-7 d-flex align-items-center justify-content-end" style={{ backgroundColor: '#e6f0ff', color: '#0072ff' }}>
                                ₩{formatCurrency(totalSales)}
                              </div>
                            </td>
                            <td className="p-2 border-0">
                              <div className="bg-secondary bg-opacity-10 rounded-3 py-2 px-3 w-100 fw-bolder text-dark fs-7 d-flex align-items-center justify-content-end">
                                {formatCurrency(totalMargin)}
                              </div>
                            </td>
                            <td className="p-2 border-0">
                              <div className="bg-secondary bg-opacity-10 rounded-3 py-2 px-2 w-100 fw-bold text-dark fs-8 d-flex align-items-center justify-content-center">
                                {totalSales > 0 ? ((totalMargin / totalSales) * 100).toFixed(0) + '%' : '0%'}
                              </div>
                            </td>
                            <td colSpan="3" className="border-0"></td>
                          </tr>
                          {data.sales.map((item, idx) => (
                            <tr key={item.id} className="hover-bg-light transition-all">
                              <td className="p-0 position-relative"><input type="text" className="form-control border-0 rounded-0 text-center fw-bolder text-dark px-3 py-2 seamless-input" style={inputStyle} value={item.type} readOnly={item.isDefault || mode === 'view'} onChange={e => handleChange('sales', item.id, 'type', e.target.value)} /></td>
                              <td className="p-0 position-relative"><input type="text" className="form-control border-0 rounded-0 text-end fw-bolder fs-7 px-3 py-2 seamless-input amount-input" style={{ ...inputStyle, color: '#0072ff' }} value={item.amount === 0 ? '' : formatCurrency(item.amount)} placeholder="0" readOnly={mode === 'view'} onChange={e => handleNumberChange('sales', item.id, 'amount', e.target.value)} /></td>
                              <td className="py-2 px-3 fw-bold fs-8 text-dark text-end bg-light bg-opacity-25">{formatCurrency(margins[idx] || 0)}</td>
                              <td className="py-2 px-2 fw-bold fs-8 text-muted text-center bg-light bg-opacity-25">{item.amount > 0 ? ((margins[idx] / item.amount) * 100).toFixed(0) + '%' : '-'}</td>
                              <td className="p-0 position-relative grid-datepicker">
                                <CommonDatePicker value={item.invoiceDate ? new Date(item.invoiceDate) : null} onChange={(d) => handleChange('sales', item.id, 'invoiceDate', formatDateToString(d))} placeholder="발행일" />
                              </td>
                              <td className="p-0 position-relative grid-datepicker">
                                <CommonDatePicker value={item.depositDate ? new Date(item.depositDate) : null} onChange={(d) => handleChange('sales', item.id, 'depositDate', formatDateToString(d))} placeholder="입금일" />
                              </td>
                              <td className="p-0 position-relative">
                                <input type="text" className="form-control border-0 rounded-0 text-dark px-3 py-2 seamless-input pe-4" style={inputStyle} placeholder="내역 입력..." value={item.note} readOnly={mode === 'view'} onChange={e => handleChange('sales', item.id, 'note', e.target.value)} />
                                {mode === 'edit' && !item.isDefault && <button className="hover-delete-btn text-danger" onClick={() => handleRemoveRow('sales', item.id)}><i className="fas fa-trash-alt"></i></button>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* 5. 매입액 상세 */}
            <div className="col-12">
              <div className="card border-0 shadow-sm rounded-4 bg-white overflow-hidden">
                <div
                  className={`card-header bg-white rounded-top-4 ${collapsed.purchases ? 'border-bottom-0' : 'border-bottom'} py-3 px-4 d-flex justify-content-between align-items-center`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => toggleCollapse('purchases')}
                >
                  <div className="d-flex align-items-center">
                    <i className={`fas fa-chevron-down text-secondary me-2 accordion-icon ${collapsed.purchases ? 'collapsed' : ''}`}></i>
                    <h5 className="fw-bolder m-0" style={{ color: '#ff758c' }}><i className="fas fa-arrow-circle-down me-2"></i>매입액 상세</h5>
                  </div>
                  {mode === 'edit' && (
                    <button className="btn btn-sm fw-bold rounded-pill px-3 shadow-sm" style={{ backgroundColor: '#fff0f3', color: '#ff758c' }} onClick={(e) => { e.stopPropagation(); handleAddRow('purchases', { type: '', amount: 0, vendor: '', receivedDate: '', paymentDate: '', note: '' }); }}><i className="fas fa-plus me-1"></i>행 추가</button>
                  )}
                </div>
                {!collapsed.purchases && (
                  <div className="card-body p-0 slide-down-fade">
                    <div className="table-responsive">
                      <table className="table table-bordered table-custom-border text-center align-middle mb-0" style={{ borderCollapse: 'collapse' }}>
                        <thead className="bg-light">
                          <tr>
                            <th rowSpan="2" className="py-2 px-2 fw-bolder fs-8 text-secondary align-middle bg-light" style={{ width: '15%' }}>구분</th>
                            <th rowSpan="2" className="py-2 px-2 fw-bolder fs-8 text-secondary align-middle bg-light" style={{ width: '15%' }}>금액</th>
                            <th rowSpan="2" className="py-2 px-2 fw-bolder fs-8 text-secondary align-middle bg-light" style={{ width: '15%' }}>발주처</th>
                            <th colSpan="3" className="py-1 px-2 fw-bolder fs-8 text-secondary bg-light border-bottom-0">세금계산서</th>
                          </tr>
                          <tr>
                            <th className="py-1 px-2 fw-bold fs-9 text-muted bg-light border-top-0" style={{ width: '12%' }}>접수일</th>
                            <th className="py-1 px-2 fw-bold fs-9 text-muted bg-light border-top-0" style={{ width: '12%' }}>대금결제일</th>
                            <th className="py-1 px-2 fw-bold fs-9 text-muted bg-light border-top-0">내역</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="border-bottom border-secondary border-opacity-10 bg-white">
                            <td className="p-2 border-0">
                              <div className="bg-secondary bg-opacity-10 rounded-3 py-2 px-2 w-100 fw-bold text-secondary fs-8 d-flex align-items-center justify-content-center">
                                총매입액
                              </div>
                            </td>
                            <td className="p-2 border-0">
                              <div className="rounded-3 py-2 px-3 w-100 fw-bolder fs-7 d-flex align-items-center justify-content-end" style={{ backgroundColor: '#fff0f3', color: '#ff758c' }}>
                                ₩{formatCurrency(totalPurchases)}
                              </div>
                            </td>
                            <td colSpan="4" className="border-0"></td>
                          </tr>
                          {purchasesData.map(item => (
                            <tr key={item.id} className="hover-bg-light transition-all">
                              <td className="p-0 position-relative"><input type="text" className="form-control border-0 rounded-0 text-center fw-bolder text-dark px-3 py-2 seamless-input" style={inputStyle} value={item.type} readOnly={item.isDefault || mode === 'view'} onChange={e => handleChange('purchases', item.id, 'type', e.target.value)} /></td>
                              {item.type === 'Service(내부)' ? (
                                <td className="py-2 px-3 fw-bolder fs-7 text-end bg-light bg-opacity-25" style={{ color: '#ff758c' }}>{formatCurrency(item.amount)}</td>
                              ) : (
                                <td className="p-0 position-relative"><input type="text" className="form-control border-0 rounded-0 text-end fw-bolder fs-7 px-3 py-2 seamless-input amount-input" style={{ ...inputStyle, color: '#ff758c' }} value={item.amount === 0 ? '' : formatCurrency(item.amount)} placeholder="0" readOnly={mode === 'view'} onChange={e => handleNumberChange('purchases', item.id, 'amount', e.target.value)} /></td>
                              )}
                              <td className="p-0 position-relative"><input type="text" className="form-control border-0 rounded-0 text-center text-dark px-3 py-2 seamless-input" style={inputStyle} placeholder="발주처 입력" value={item.vendor} readOnly={mode === 'view'} onChange={e => handleChange('purchases', item.id, 'vendor', e.target.value)} /></td>
                              <td className="p-0 position-relative grid-datepicker">
                                <CommonDatePicker value={item.receivedDate ? new Date(item.receivedDate) : null} onChange={(d) => handleChange('purchases', item.id, 'receivedDate', formatDateToString(d))} placeholder="접수일" disabled={mode === 'view'} />
                              </td>
                              <td className="p-0 position-relative grid-datepicker">
                                <CommonDatePicker value={item.paymentDate ? new Date(item.paymentDate) : null} onChange={(d) => handleChange('purchases', item.id, 'paymentDate', formatDateToString(d))} placeholder="결제일" disabled={mode === 'view'} />
                              </td>
                              <td className="p-0 position-relative">
                                <input type="text" className="form-control border-0 rounded-0 text-dark px-3 py-2 seamless-input pe-4" style={inputStyle} placeholder="내역 입력..." value={item.note} readOnly={mode === 'view'} onChange={e => handleChange('purchases', item.id, 'note', e.target.value)} />
                                {mode === 'edit' && !item.isDefault && <button className="hover-delete-btn text-danger" onClick={() => handleRemoveRow('purchases', item.id)}><i className="fas fa-trash-alt"></i></button>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* 6. 서비스 매출(공수) */}
            <div className="col-12">
              <div className="card border-0 shadow-sm rounded-4 bg-white overflow-hidden">
                <div
                  className={`card-header bg-white rounded-top-4 ${collapsed.labor ? 'border-bottom-0' : 'border-bottom'} py-3 px-4 d-flex justify-content-between align-items-center`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => toggleCollapse('labor')}
                >
                  <div className="d-flex align-items-center">
                    <i className={`fas fa-chevron-down text-secondary me-2 accordion-icon ${collapsed.labor ? 'collapsed' : ''}`}></i>
                    <h5 className="fw-bolder m-0" style={{ color: '#00b894' }}><i className="fas fa-user-clock me-2"></i>서비스 매출 (공수 및 인건비)</h5>
                  </div>
                  <button className="btn btn-sm fw-bold rounded-pill px-3 shadow-sm" style={{ backgroundColor: '#e6f9f0', color: '#00b894' }} onClick={(e) => { e.stopPropagation(); handleAddRow('labor', { type: '', assignee: '', mmPlan: 0, period: '', unitPrice: 0, mmActual: 0 }); }}><i className="fas fa-plus me-1"></i>행 추가</button>
                </div>
                {!collapsed.labor && (
                  <div className="card-body p-0 slide-down-fade">
                    <div className="table-responsive">
                      <table className="table table-bordered table-custom-border text-center align-middle mb-0" style={{ borderCollapse: 'collapse' }}>
                        <thead className="bg-light">
                          <tr>
                            <th className="py-2 px-2 fw-bolder fs-8 text-secondary bg-light text-center" style={{ width: '15%' }}>구분</th>
                            <th className="py-2 px-2 fw-bolder fs-8 text-secondary bg-light text-center" style={{ width: '15%' }}>담당</th>
                            <th className="py-2 px-2 fw-bolder fs-8 text-secondary bg-light text-center" style={{ width: '90px', minWidth: '90px' }}>M/M</th>
                            <th className="py-2 px-2 fw-bolder fs-8 text-secondary bg-light text-center" style={{ width: '200px', minWidth: '200px' }}>투입 기간</th>
                            <th className="py-2 px-2 fw-bolder fs-8 text-secondary bg-light text-center" style={{ width: '150px', minWidth: '150px' }}>단가</th>
                            <th className="py-2 px-2 fw-bolder fs-8 text-secondary bg-light text-center" style={{ width: '90px', minWidth: '90px' }}>실 투입MM</th>
                            <th className="py-2 px-2 fw-bolder fs-8 text-secondary bg-light text-center" style={{ width: '150px', minWidth: '150px' }}>금액</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.labor.map(item => {
                            const periodParts = item.period ? item.period.split('~') : [];
                            const startDateStr = periodParts[0] ? periodParts[0].trim() : '';
                            const endDateStr = periodParts[1] ? periodParts[1].trim() : '';
                            const startDate = startDateStr && startDateStr.length === 10 ? new Date(startDateStr) : null;
                            const endDate = endDateStr && endDateStr.length === 10 ? new Date(endDateStr) : null;
                            return (
                              <tr key={item.id} className="hover-bg-light transition-all">
                                <td className="p-0 position-relative"><input type="text" className="form-control border-0 rounded-0 text-center fw-bolder text-dark px-3 py-2 seamless-input" style={inputStyle} value={item.type} onChange={e => handleChange('labor', item.id, 'type', e.target.value)} /></td>
                                <td className="p-0 position-relative"><input type="text" className="form-control border-0 rounded-0 text-center text-dark px-3 py-2 seamless-input" style={inputStyle} value={item.assignee} onChange={e => handleChange('labor', item.id, 'assignee', e.target.value)} /></td>
                                <td className="p-0 position-relative"><input type="number" className="form-control border-0 rounded-0 text-center text-muted px-3 py-2 seamless-input" style={inputStyle} value={item.mmPlan === 0 ? '' : item.mmPlan} placeholder="0" onChange={e => handleChange('labor', item.id, 'mmPlan', e.target.value)} /></td>
                                <td className="p-0 position-relative">
                                  <div className="d-flex align-items-center justify-content-center px-1 pms-datepicker-container">
                                    <CommonDatePicker
                                      value={startDate}
                                      onChange={(d) => handleChange('labor', item.id, 'period', `${formatDateToString(d)} ~ ${endDateStr}`)}
                                      className="form-control border-0 rounded-0 bg-transparent text-center px-1 text-dark seamless-input fs-9"
                                      placeholder="시작일"
                                    />
                                    <span className="text-muted mx-1">~</span>
                                    <CommonDatePicker
                                      value={endDate}
                                      onChange={(d) => handleChange('labor', item.id, 'period', `${startDateStr} ~ ${formatDateToString(d)}`)}
                                      className="form-control border-0 rounded-0 bg-transparent text-center px-1 text-dark seamless-input fs-9"
                                      placeholder="종료일"
                                    />
                                  </div>
                                </td>
                                <td className="p-0 position-relative"><input type="text" className="form-control border-0 rounded-0 text-end fw-bold text-dark px-3 py-2 seamless-input" style={inputStyle} value={item.unitPrice === 0 ? '' : formatCurrency(item.unitPrice)} placeholder="0" onChange={e => handleNumberChange('labor', item.id, 'unitPrice', e.target.value)} /></td>
                                <td className="p-0 position-relative"><input type="number" className="form-control border-0 rounded-0 text-center fw-bold text-dark px-3 py-2 seamless-input" style={inputStyle} value={item.mmActual === 0 ? '' : item.mmActual} placeholder="0" onChange={e => handleChange('labor', item.id, 'mmActual', e.target.value)} /></td>
                                <td className="py-2 px-3 text-end fw-bold fs-7 text-dark bg-light bg-opacity-25 position-relative">
                                  {formatCurrency(item.unitPrice * item.mmActual)}
                                  <button className="hover-delete-btn text-danger" onClick={() => data.labor.length > 1 ? handleRemoveRow('labor', item.id) : alert('최소 한 개의 항목이 필요합니다.')}><i className="fas fa-trash-alt"></i></button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                        <tfoot className="bg-light bg-opacity-50">
                          <tr>
                            <td colSpan="2" className="py-2 text-secondary fw-bolder text-end pe-4 fs-8">총투입공수</td>
                            <td className="py-2 fw-bolder text-dark fs-7 text-center">{totalLaborMM}</td>
                            <td></td>
                            <td className="py-2 fw-bolder text-dark fs-8 text-center">100%</td>
                            <td></td>
                            <td className="py-2 px-3 text-end"><span className="fw-bolder fs-6" style={{ color: '#00b894' }}>₩{formatCurrency(totalLaborAmount)}</span></td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* 7. 실투입현황 */}
            <div className="col-12 mb-4">
              <div className="card border-0 shadow-sm rounded-4 bg-white overflow-hidden">
                <div
                  className={`card-header bg-white rounded-top-4 ${collapsed.actualLabor ? 'border-bottom-0' : 'border-bottom'} py-3 px-4 d-flex justify-content-between align-items-center`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => toggleCollapse('actualLabor')}
                >
                  <div className="d-flex align-items-center">
                    <i className={`fas fa-chevron-down text-secondary me-2 accordion-icon ${collapsed.actualLabor ? 'collapsed' : ''}`}></i>
                    <h5 className="fw-bolder m-0" style={{ color: '#764ba2' }}><i className="fas fa-users me-2"></i>실투입현황</h5>
                  </div>
                  <div className="d-flex gap-2">
                    <button className="btn btn-sm fw-bold rounded-pill px-3 shadow-sm border" style={{ borderColor: '#764ba2', color: '#764ba2', backgroundColor: 'transparent' }} onClick={handleLoadActualLabor}>
                      <i className="fas fa-laptop-code me-2"></i>인력 및 실투입 Man Day 자동 합산
                    </button>
                    <button className="btn btn-sm fw-bold rounded-pill px-3 shadow-sm" style={{ backgroundColor: '#f4e6ff', color: '#764ba2' }} onClick={(e) => { e.stopPropagation(); handleAddRow('actualLabor', { role: '', assignee: '', mmActual: 0 }); }}>
                      <i className="fas fa-plus me-1"></i>행 추가
                    </button>
                  </div>
                </div>
                {!collapsed.actualLabor && (
                  <div className="card-body p-0 slide-down-fade">
                    <div className="table-responsive">
                      <table className="table table-bordered table-custom-border text-center align-middle mb-0 actual-labor-table" style={{ borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                        <thead className="bg-light">
                          <tr>
                            <th className="py-2 px-2 fw-bolder fs-8 text-secondary bg-light text-center" style={{ width: '15%' }}>역할</th>
                            <th className="py-2 px-2 fw-bolder fs-8 text-secondary bg-light text-center" style={{ width: '15%' }}>성명</th>
                            <th className="py-2 px-2 fw-bolder fs-8 text-secondary bg-light text-center">실투입일(Man Day)</th>
                            <th className="p-0 border-0" style={{ width: '10px', backgroundColor: '#f8f9fa' }}></th>
                            <th className="py-2 px-2 fw-bolder fs-8 text-secondary bg-light text-center" style={{ width: '15%' }}>역할</th>
                            <th className="py-2 px-2 fw-bolder fs-8 text-secondary bg-light text-center" style={{ width: '15%' }}>성명</th>
                            <th className="py-2 px-2 fw-bolder fs-8 text-secondary bg-light text-center">실투입일(Man Day)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Array.from({ length: Math.ceil((data.actualLabor?.length || 0) / 2) }).map((_, rowIndex) => {
                            const item1 = data.actualLabor[rowIndex * 2];
                            const item2 = data.actualLabor[rowIndex * 2 + 1];

                            const renderCell = (item) => {
                              if (!item) return (
                                <React.Fragment key="empty">
                                  <td className="p-0 bg-light"></td>
                                  <td className="p-0 bg-light"></td>
                                  <td className="p-0 bg-light"></td>
                                </React.Fragment>
                              );
                              return (
                                <React.Fragment key={item.id}>
                                  <td className="p-0 position-relative">
                                    <input type="text" className="form-control border-0 rounded-0 text-center text-dark fw-bold px-3 py-2 seamless-input" value={item.role || ''} onChange={e => handleChange('actualLabor', item.id, 'role', e.target.value)} placeholder="역할 (예: 책임자(PM), 담당자)" />
                                  </td>
                                  <td className="p-0 position-relative">
                                    <input type="text" className="form-control border-0 rounded-0 text-center text-dark fw-bold px-3 py-2 seamless-input" value={item.assignee || ''} onChange={e => handleChange('actualLabor', item.id, 'assignee', e.target.value)} placeholder="이름" />
                                    {item.assignee && Number(item.mmActual) > 0 && (
                                      <button 
                                        type="button"
                                        className="btn p-1 fs-7 text-secondary hover-text-primary position-absolute end-0 top-50 translate-middle-y me-1" 
                                        onClick={() => openActualLaborModal(item.assignee)} 
                                        title="상세내역 보기"
                                        style={{ zIndex: 5 }}
                                      >
                                        <i className="fas fa-search"></i>
                                      </button>
                                    )}
                                  </td>
                                  <td className="p-0 position-relative">
                                    <input type="number" className="form-control border-0 rounded-0 text-center fw-bold px-3 py-2 seamless-input pe-4" style={{ color: '#764ba2' }} value={(!item.role && !item.assignee) ? '' : (item.mmActual || 0)} placeholder="0" onChange={e => handleChange('actualLabor', item.id, 'mmActual', e.target.value)} />
                                    <button className="hover-delete-btn text-danger" onClick={() => handleRemoveRow('actualLabor', item.id)}>
                                      <i className="fas fa-trash-alt"></i>
                                    </button>
                                  </td>
                                </React.Fragment>
                              );
                            };

                            return (
                              <tr key={rowIndex} className="hover-bg-light transition-all">
                                {renderCell(item1)}
                                <td className="p-0 border-0" style={{ backgroundColor: '#f8f9fa' }}></td>
                                {renderCell(item2)}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* 8. 기타 비용 */}
            <div className="col-12">
              <div className="card border-0 shadow-sm rounded-4 bg-white overflow-hidden">
                <div
                  className={`card-header bg-white rounded-top-4 ${collapsed.expenses ? 'border-bottom-0' : 'border-bottom'} py-3 px-4 d-flex justify-content-between align-items-center`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => toggleCollapse('expenses')}
                >
                  <div className="d-flex align-items-center">
                    <i className={`fas fa-chevron-down text-secondary me-2 accordion-icon ${collapsed.expenses ? 'collapsed' : ''}`}></i>
                    <h5 className="fw-bolder m-0" style={{ color: '#00cec9' }}><i className="fas fa-coins me-2"></i>기타 비용</h5>
                  </div>
                  {mode === 'edit' && (
                    <button className="btn btn-sm fw-bold rounded-pill px-3 shadow-sm" style={{ backgroundColor: '#e0f7fa', color: '#00cec9' }} onClick={(e) => { e.stopPropagation(); handleAddRow('expenses', { type: '', amount: 0, actual: 0, note: '' }); }}><i className="fas fa-plus me-1"></i>행 추가</button>
                  )}
                </div>
                {!collapsed.expenses && (
                  <div className="card-body p-0 slide-down-fade">
                    <div className="table-responsive">
                      <table className="table table-bordered table-custom-border text-center align-middle mb-0" style={{ borderCollapse: 'collapse' }}>
                        <thead className="bg-light">
                          <tr>
                            <th rowSpan="2" className="py-2 px-2 fw-bolder fs-8 text-secondary align-middle bg-light" style={{ width: '15%' }}>구분</th>
                            <th rowSpan="2" className="py-2 px-2 fw-bolder fs-8 text-secondary align-middle bg-light" style={{ width: '15%' }}>금액</th>
                            <th colSpan="4" className="py-1 px-2 fw-bolder fs-8 text-secondary bg-light border-bottom-0">내역</th>
                          </tr>
                          <tr>
                            <th className="py-1 px-2 fw-bold fs-9 text-muted bg-light border-top-0" style={{ width: '15%' }}>실사용금액</th>
                            <th className="py-1 px-2 fw-bold fs-9 text-muted bg-light border-top-0" style={{ width: '12%' }}>차액</th>
                            <th className="py-1 px-2 fw-bold fs-9 text-muted bg-light border-top-0" style={{ width: '12%' }}>잔액</th>
                            <th className="py-1 px-2 fw-bold fs-9 text-muted bg-light border-top-0">비고</th>
                          </tr>
                        </thead>
                        <tbody>
                          {expensesData.map(item => (
                            <tr key={item.id} className="hover-bg-light transition-all">
                              <td className="p-0 position-relative"><input type="text" className="form-control border-0 rounded-0 text-center fw-bolder text-dark px-3 py-2 seamless-input" style={inputStyle} value={item.type} readOnly={item.isDefault || mode === 'view'} onChange={e => handleChange('expenses', item.id, 'type', e.target.value)} /></td>
                              <td className="p-0 position-relative"><input type="text" className="form-control border-0 rounded-0 text-end fw-bold text-dark fs-7 px-3 py-2 seamless-input amount-input" style={inputStyle} value={item.amount === 0 ? '' : formatCurrency(item.amount)} placeholder="0" readOnly={mode === 'view'} onChange={e => handleNumberChange('expenses', item.id, 'amount', e.target.value)} /></td>
                              <td className="p-0 position-relative"><input type="text" className="form-control border-0 rounded-0 text-end fw-bolder text-danger fs-7 px-3 py-2 seamless-input amount-input" style={inputStyle} value={item.actual === 0 ? '' : formatCurrency(item.actual)} placeholder="0" readOnly={mode === 'view'} onChange={e => handleNumberChange('expenses', item.id, 'actual', e.target.value)} /></td>
                              <td className="py-2 px-3 text-end fw-bold fs-8 text-dark bg-light bg-opacity-25">{formatCurrency(item.amount - item.actual)}</td>
                              <td className="py-2 px-3 text-end fw-bold fs-8 text-dark bg-light bg-opacity-25">{formatCurrency(item.amount - item.actual)}</td>
                              <td className="p-0 position-relative">
                                <input type="text" className="form-control border-0 rounded-0 text-dark px-3 py-2 seamless-input pe-4" style={inputStyle} placeholder={item.type === '영업지원비' ? '마진금액의 10%' : '비고 입력...'} value={item.note} readOnly={mode === 'view'} onChange={e => handleChange('expenses', item.id, 'note', e.target.value)} />
                                {mode === 'edit' && !item.isDefault && <button className="hover-delete-btn text-danger" onClick={() => handleRemoveRow('expenses', item.id)}><i className="fas fa-trash-alt"></i></button>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="bg-light bg-opacity-50 border-top border-secondary border-opacity-25">
                          <tr>
                            <td className="py-2 text-secondary fw-bolder text-end pe-4 fs-8">합계</td>
                            <td className="py-2 px-3 text-end"><span className="fw-bolder fs-7" style={{ color: '#00cec9' }}>₩{formatCurrency(totalExpenses)}</span></td>
                            <td className="py-2 px-3 text-end"><span className="fw-bolder text-danger fs-7">₩{formatCurrency(totalActualExpenses)}</span></td>
                            <td colSpan="3"></td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* 9. 특이사항 및 메모 */}
            <div className="col-12 mt-3">
              <div className="card border-0 shadow-sm rounded-4 bg-white overflow-hidden">
                <div
                  className={`card-header bg-white rounded-top-4 ${collapsed.memo ? 'border-bottom-0' : 'border-bottom'} py-3 px-4 d-flex align-items-center`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => toggleCollapse('memo')}
                >
                  <i className={`fas fa-chevron-down text-secondary me-2 accordion-icon ${collapsed.memo ? 'collapsed' : ''}`}></i>
                  <h5 className="fw-bolder text-dark m-0"><i className="fas fa-sticky-note text-warning me-2"></i>특이사항 및 메모</h5>
                </div>
                {!collapsed.memo && (
                  <div className="card-body px-4 pb-4 pt-3 slide-down-fade">
                    <textarea
                      className="form-control border-secondary border-opacity-25 rounded-3 shadow-sm p-3 lh-lg"
                      rows="5"
                      value={data.description || ''}
                      onChange={e => setData({ ...data, description: e.target.value })}
                      placeholder={mode === 'view' ? "" : "프로젝트 설명 및 특이사항을 입력하세요"}
                      disabled={mode === 'view'}
                      style={mode === 'view' ? {
                        backgroundColor: '#f8f9fa',
                        border: '1px solid #e9ecef',
                        pointerEvents: 'none',
                        color: '#6c757d',
                        fontWeight: 600,
                        resize: 'none',
                        boxShadow: 'none'
                      } : { backgroundColor: '#fff' }}
                    ></textarea>
                  </div>
                )}
              </div>
            </div>

          </div>
        </>
      )}

      {/* 실투입 상세내역 모달 */}
      {/* 실투입 상세내역 모달 */}
      {actualLaborModal.isOpen && ReactDOM.createPortal(
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 99999 }} onClick={closeActualLaborModal}>
          {/* modal-dialog-scrollable 추가하여 창이 작아져도 위쪽이 잘리지 않게 방지 */}
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable" onClick={e => e.stopPropagation()}>
            <div className="modal-content border-0 shadow-lg rounded-4 overflow-hidden">
              <div className="modal-header border-bottom bg-light py-3 px-4 rounded-top-4">
                <h5 className="modal-title fw-bolder fs-5" style={{ color: '#2d3748' }}>
                  <i className="fas fa-user-circle me-2 text-primary"></i>[{actualLaborModal.userName}] 실투입 상세 내역
                </h5>
                <button type="button" className="btn-close" onClick={closeActualLaborModal}></button>
              </div>
              <div className="modal-body py-3 px-4 bg-white">
                
                {/* 상단 프리미엄 요약 대시보드 카드 (컴팩트 & 좌측 아이콘 & 우측 하단 토글버튼) */}
                <div className="mb-3 d-flex justify-content-between align-items-end pt-3 pb-2 px-4 rounded-4 text-white shadow-sm" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                  <div className="d-flex align-items-center mb-1">
                    <div className="bg-white bg-opacity-25 rounded-circle d-flex align-items-center justify-content-center me-3 shadow-sm" style={{ width: '42px', height: '42px' }}>
                      <i className="fas fa-chart-line fs-4 text-white"></i>
                    </div>
                    <div>
                      <h6 className="fw-bold mb-0 text-white text-opacity-75 fs-8">전체 누적 투입일</h6>
                      <h3 className="fw-bolder mb-0 text-white mt-1">{totalUserRealMD} M/D</h3>
                    </div>
                  </div>
                  <div>
                    <button 
                      type="button" 
                      className="btn btn-sm btn-light text-primary rounded-pill fw-bolder px-3 py-1 shadow-sm d-flex align-items-center"
                      onClick={() => setIsAllExpanded(!isAllExpanded)}
                      style={{ fontSize: '0.8rem' }}
                    >
                      {isAllExpanded ? <><i className="fas fa-chevron-up me-1"></i>전체 접기</> : <><i className="fas fa-chevron-down me-1"></i>전체 펼치기</>}
                    </button>
                  </div>
                </div>

                {Object.keys(groupedRealData).length > 0 ? (
                  <div className="accordion" id="realDataAccordion">
                    {Object.entries(groupedRealData).map(([folder, folderTasks], idx) => {
                      const folderTotal = folderTasks.reduce((sum, t) => sum + t.md, 0);
                      return (
                        <div className="accordion-item border border-light mb-2 shadow-sm rounded-3 overflow-hidden" key={idx}>
                          <h2 className="accordion-header bg-white">
                            <button className={`accordion-button bg-light bg-opacity-50 shadow-none text-dark border-bottom py-2 px-4 ${isAllExpanded ? '' : 'collapsed'}`} type="button" data-bs-toggle="collapse" data-bs-target={`#collapse${idx}`} aria-expanded={isAllExpanded}>
                              <div className="d-flex align-items-center w-100">
                                <div className="bg-primary bg-opacity-10 text-primary rounded-3 me-3 d-flex align-items-center justify-content-center" style={{ width: '32px', height: '32px' }}>
                                  <i className="far fa-folder-open fs-6"></i>
                                </div>
                                <span className="fs-7 fw-bolder text-dark">{folder}</span>
                                <span className="badge bg-secondary bg-opacity-10 text-secondary ms-2 rounded-pill px-2 py-1 fs-8">{folderTasks.length}건</span>
                                
                                {/* 우측 M/D 정렬 기준점 (Width 고정) */}
                                <div className="ms-auto fw-bolder fs-7 pe-1" style={{ color: '#764ba2', width: '100px', textAlign: 'right' }}>
                                  총 {folderTotal.toFixed(1)} M/D
                                </div>
                              </div>
                            </button>
                          </h2>
                          <div id={`collapse${idx}`} className={`accordion-collapse collapse ${isAllExpanded ? 'show' : ''}`}>
                            <div className="accordion-body p-0 bg-white">
                              <ul className="list-group list-group-flush">
                                {folderTasks.map((t, i) => (
                                  <li className="list-group-item d-flex justify-content-between align-items-center py-2 px-4 bg-transparent border-light border-bottom" key={i}>
                                    <div className="d-flex align-items-start ms-4">
                                      <span className="text-secondary me-2 fw-bold" style={{ opacity: 0.4 }}>└</span>
                                      <div className="d-flex flex-column">
                                        <span className="fw-bold text-secondary fs-7">{t.title}</span>
                                        <span className="text-muted fw-semibold mt-1" style={{ fontSize: '0.7rem' }}><i className="far fa-calendar-alt me-1 opacity-75"></i>{t.start} ~ {t.end}</span>
                                      </div>
                                    </div>
                                    <div className="d-flex align-items-center gap-2 pe-1">
                                      <span className={`badge ${t.status.includes('완료') ? 'bg-success' : t.status.includes('지연') ? 'bg-danger' : t.status.includes('진행중') ? 'bg-primary' : 'bg-secondary'} bg-opacity-10 ${t.status.includes('완료') ? 'text-success' : t.status.includes('지연') ? 'text-danger' : t.status.includes('진행중') ? 'text-primary' : 'text-secondary'} px-2 py-1 rounded-pill fw-bold fs-8`}>{t.status}</span>
                                      <span className="fw-bolder fs-7" style={{ color: '#4a5568', width: '100px', textAlign: 'right' }}>{t.md.toFixed(1)} M/D</span>
                                    </div>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-4 text-muted bg-light rounded-3">
                    <i className="fas fa-info-circle fs-2 mb-2 text-secondary opacity-50"></i>
                    <h6 className="fw-bold text-dark">데이터가 없습니다</h6>
                    <p className="mb-0 fs-8">해당 사용자로 배정된 업무 내역이 존재하지 않습니다.</p>
                  </div>
                )}
              </div>
              <div className="modal-footer border-top bg-light rounded-bottom-4 py-2 px-4">
                <button type="button" className="btn btn-secondary btn-sm px-4 fw-bold rounded-pill shadow-sm" onClick={closeActualLaborModal}>닫기</button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
